import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom';
import {
  Maximize2,
  Send,
  Plus,
  Search,
  Settings,
  LogOut,
  User,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Trash2,
  RefreshCw,
  Check,
  Sparkles,
  Image,
  Info,
  Lock,
  Menu,
  X,
  Users
} from 'lucide-react';
import { useTheme } from './context/ThemeContext.tsx';
import { useIsMobile } from './hooks/useIsMobile.ts';

import Sidebar from './components/Sidebar.tsx';
import LLMSelectionModal from './components/LLMSelectionModal.tsx';
import MemoryModal from './components/MemoryModal.tsx';
import MemoryHubModal from './components/MemoryHubModal.tsx';
import UserPersonaModal from './components/UserPersonaModal.tsx';
import LimitReachedModal from './components/LimitReachedModal.tsx';
import PricingModal from './components/PricingModal.tsx';
import { API_URL, API_BASE_URL } from './config/api.ts';

// Define interfaces for our data structures
interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: Date;
}

interface Chat {
  _id: string;
  title: string;
  characterId: {
    _id: string;
    name: string;
    avatar: string;
    description: string;
  };
  messages: Message[];
  createdAt: string; // Fecha de creación del chat (MongoDB timestamps)
  updatedAt: string; // Fecha de última actualización
}

interface Character {
  _id: string;
  name: string;
  description: string;
  greeting?: string;
  personality: string;
  systemPrompt: string;
  avatar: string;
  backgroundImage: string;
  backgroundSettings?: {
    desktop: { positionX: number; positionY: number; zoom: number; };
    mobile: { positionX: number; positionY: number; zoom: number; };
  };
  tags: string[];
  isDefault: boolean;
  createdBy: string;
  userId?: {
    _id: string;
    username: string;
  };
  hidePersonality?: boolean;
  hideCreator?: boolean;
}

const backgroundImage = 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/erza-GgLDNYG7BpWQjSiCdY1Yya2Lrhgjxv.png'
const galleryImages = Array.from({ length: 12 }, (_, i) => `/placeholder.svg?height=100&width=100&text=Image${i + 1}`)

interface AppProps {
  initialCharacterId?: string | null;
  initialChatId?: string | null;
  onLoginSuccess?: () => void;
  isAlreadyLoggedIn?: boolean;
}

export default function App({ initialCharacterId, initialChatId, onLoginSuccess, isAlreadyLoggedIn }: AppProps) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const isMobile = useIsMobile();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(isAlreadyLoggedIn || false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [isRegistering, setIsRegistering] = useState(false)
  const [email, setEmail] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [registerError, setRegisterError] = useState('')

  // Estado para el usuario logueado
  const [loggedUser, setLoggedUser] = useState('')
  const [userStats, setUserStats] = useState<any>(null)

  const [chats, setChats] = useState<Chat[]>([])
  const [activeChat, setActiveChat] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [showInfoPanel, setShowInfoPanel] = useState(false)
  const [showGallery, setShowGallery] = useState(false)

  // Renamed showChatsModal to showChatsModal for clarity, and added new navigation sidebar state
  const [showChatsModal, setShowChatsModal] = useState(false)
  const [showNavigationSidebar, setShowNavigationSidebar] = useState(false)
  const [showLLMModal, setShowLLMModal] = useState(false)

  // Estados para el sistema de personajes
  const [characters, setCharacters] = useState<Character[]>([])
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null)
  const [charactersLoading, setCharactersLoading] = useState(false)
  const [charactersInitialized, setCharactersInitialized] = useState(false)
  const carouselRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  // Estado para regeneración
  const [isRegenerating, setIsRegenerating] = useState(false)

  // Estado para el carrusel de personajes colapsable
  const [showCharacterCarousel, setShowCharacterCarousel] = useState(false)

  // Estado para el modal de información del personaje
  const [showCharacterInfoModal, setShowCharacterInfoModal] = useState(false)

  // Estados para el efecto de typing del asistente
  const [isAssistantTyping, setIsAssistantTyping] = useState(false)
  const [typingContent, setTypingContent] = useState('')
  const [fullResponseContent, setFullResponseContent] = useState('')

  // Estados para edición de mensajes
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null)
  const [editingContent, setEditingContent] = useState('')

  // Estados para eliminación de mensajes
  const [deletingMessageIndex, setDeletingMessageIndex] = useState<number | null>(null)

  // Estados para navegación de versiones regeneradas
  const [messageVersions, setMessageVersions] = useState<{ [messageIndex: number]: string[] }>({})
  const [currentVersionIndex, setCurrentVersionIndex] = useState<{ [messageIndex: number]: number }>({})

  // 🤖 LLM Model Selection
  const LLM_MODELS = [
    { id: 'mistralai/mistral-small-3.2-24b-instruct', name: 'Mistral Nemo (Small 3.2)', price: '1 message', cost: 1, icon: '🌀' },
    { id: 'x-ai/grok-4.1-fast', name: 'Grok 4.1 Fast', price: '3 messages', cost: 3, icon: '⚡' },
    { id: 'deepseek/deepseek-v3.2', name: 'DeepSeek V3.2', price: '2 messages', cost: 2, icon: '🔮' },
  ] as const;

  // Load persisted settings from localStorage
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    const saved = localStorage.getItem('selectedLLMModel');
    return saved || LLM_MODELS[0].id;
  });

  // State for background blur toggle with persistence
  const [backgroundBlurEnabled, setBackgroundBlurEnabled] = useState(() => {
    const saved = localStorage.getItem('backgroundBlurEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  // State for full screen chat toggle with persistence
  const [fullScreenChatEnabled, setFullScreenChatEnabled] = useState(() => {
    const saved = localStorage.getItem('fullScreenChatEnabled');
    return saved !== null ? saved === 'true' : false;
  });

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('selectedLLMModel', selectedModel);
  }, [selectedModel]);

  useEffect(() => {
    localStorage.setItem('backgroundBlurEnabled', String(backgroundBlurEnabled));
  }, [backgroundBlurEnabled]);

  useEffect(() => {
    localStorage.setItem('fullScreenChatEnabled', String(fullScreenChatEnabled));
  }, [fullScreenChatEnabled]);

  // State for disabling typing effect
  const [disableTypingEffect, setDisableTypingEffect] = useState(() => {
    const saved = localStorage.getItem('disableTypingEffect');
    return saved !== null ? saved === 'true' : false;
  });

  useEffect(() => {
    localStorage.setItem('disableTypingEffect', String(disableTypingEffect));
  }, [disableTypingEffect]);

  // User Persona State & Logic
  const [showUserPersonaModal, setShowUserPersonaModal] = useState(false);
  const [activeUserPersona, setActiveUserPersona] = useState<{ title: string, content: string } | null>(null);
  const [userPersonaEnabled, setUserPersonaEnabled] = useState(() => localStorage.getItem('userPersonaEnabled') === 'true');

  const fetchActivePersona = useCallback(async () => {
    try {
      if (!isLoggedIn) return;
      const res = await fetch(`${API_URL}/user/personas`, { credentials: 'include' });
      const data = await res.json();
      if (data.success && data.personas) {
        const active = data.personas.find((p: any) => p.isActive);
        setActiveUserPersona(active || null);
      }
    } catch (err) {
      console.error('Error fetching persona:', err);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) fetchActivePersona();
  }, [isLoggedIn, fetchActivePersona]);

  // Refresh enabled state when modal closes
  useEffect(() => {
    if (!showUserPersonaModal) {
      setUserPersonaEnabled(localStorage.getItem('userPersonaEnabled') === 'true');
      fetchActivePersona();
    }
  }, [showUserPersonaModal, fetchActivePersona]);

  // State for message limit modal
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [showMemoryHubModal, setShowMemoryHubModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Función para cargar versiones desde mensajes del backend
  const loadVersionsFromMessages = useCallback((messages: any[]) => {
    const versions: { [messageIndex: number]: string[] } = {}
    const indices: { [messageIndex: number]: number } = {}

    messages.forEach((message, index) => {
      if (message.role === 'assistant' && message.versions && message.versions.length > 1) {
        versions[index] = message.versions.map((v: any) => v.content)
        indices[index] = message.currentVersionIndex || 0
      }
    })

    setMessageVersions(versions)
    setCurrentVersionIndex(indices)
    console.log('📊 Versiones cargadas desde backend:', { versions, indices })
  }, [])

  // Componente de animación de puntos de carga
  const LoadingDots = () => (
    <div style={{
      display: 'flex',
      gap: '4px',
      alignItems: 'center'
    }}>
      <span style={{
        animation: 'pulse 1.4s ease-in-out infinite',
        animationDelay: '0s'
      }}>●</span>
      <span style={{
        animation: 'pulse 1.4s ease-in-out infinite',
        animationDelay: '0.2s'
      }}>●</span>
      <span style={{
        animation: 'pulse 1.4s ease-in-out infinite',
        animationDelay: '0.4s'
      }}>●</span>
      <style>{`
        @keyframes pulse {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }
      `}</style>
    </div>
  )

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setIsLoggedIn(true);
        setLoggedUser(username); // Preservar el nombre del usuario
        setLoginError('');
        fetchChats();
        initializeCharactersOnce();
        fetchUserProfile();

        // Notificar al AppRouter que el login fue exitoso
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        setLoginError(data.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Error logging in:', error);
      setLoginError('Error logging in. Please try again.');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, email, password }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setIsRegistering(false);
        setLoggedUser(username); // Preservar el nombre del usuario
        setRegisterError('');
        setIsLoggedIn(true);
        fetchChats();
        initializeCharactersOnce();
        fetchUserProfile();

        // Notificar al AppRouter que el registro fue exitoso
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        setRegisterError(data.message || 'Error registering. Please try again.');
      }
    } catch (error) {
      console.error('Error registering:', error);
      setRegisterError('Error registering. Please try again.');
    }
  };

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/user`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.user) {
        setLoggedUser(data.user.username);
        setUserStats(data.user);
        console.log('👤 App.tsx - User profile updated:', data.user);
        console.log('🔑 App.tsx - isOAuthUser:', data.user.isOAuthUser, 'hasPassword:', data.user.hasPassword);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, []);

  const fetchMessages = useCallback(async (chatId: string) => {
    try {
      const response = await fetch(`${API_URL}/messages/${chatId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages);
        // Reset typing/regenerating states when loading new messages
        setIsAssistantTyping(false);
        setTypingContent('');
        setIsRegenerating(false);
        // 🔥 CRUCIAL: Cargar versiones desde mensajes del backend
        loadVersionsFromMessages(data.messages);
        console.log('📋 Mensajes y versiones cargados desde backend');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [loadVersionsFromMessages]);

  const fetchChats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/chats`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setChats(data.chats);

        // Logic to determine which chat to open
        if (data.chats.length > 0) {
          if (initialChatId) {
            // 🎯 PRIORITY 1: Specific Chat ID requested (e.g. from "New Chat" button)
            const targetChat = data.chats.find((c: Chat) => c._id === initialChatId);
            if (targetChat) {
              console.log('🎯 Opening specific chat requested via URL:', initialChatId);
              setActiveChat(targetChat._id);

              // If messages are already available in the chat list, use them immediately
              if (targetChat.messages && targetChat.messages.length > 0) {
                console.log('📦 Messages already present in chat list, skipping fetchMessages');
                setMessages(targetChat.messages);
                setIsAssistantTyping(false);
                setTypingContent('');
                setIsRegenerating(false);
                loadVersionsFromMessages(targetChat.messages);
              } else {
                fetchMessages(targetChat._id);
              }
              return; // Exit early
            }
          }

          if (initialCharacterId) {
            // Priority 2: Character requested (open most recent)
            const characterChats = data.chats.filter((chat: Chat) => chat.characterId && chat.characterId._id === initialCharacterId);

            if (characterChats.length > 0) {
              // Found existing chat for this character
              // Sort to ensure we get the absolute latest if backend sort is unreliable
              const sortedChats = characterChats.sort((a: Chat, b: Chat) => b._id.localeCompare(a._id));
              const lastChat = sortedChats[0];
              setActiveChat(lastChat._id);
              if (lastChat.messages && lastChat.messages.length > 0) {
                setMessages(lastChat.messages);
                setIsAssistantTyping(false);
                setTypingContent('');
                setIsRegenerating(false);
                loadVersionsFromMessages(lastChat.messages);
              } else {
                fetchMessages(lastChat._id);
              }
            } else {
              // No existing chat for this character
              setActiveChat(null);
              setMessages([]);
            }
          } else {
            // Priority 3: Default most recent overall
            setActiveChat(data.chats[0]._id);
            if (data.chats[0].messages && data.chats[0].messages.length > 0) {
              setMessages(data.chats[0].messages);
              setIsAssistantTyping(false);
              setTypingContent('');
              setIsRegenerating(false);
              loadVersionsFromMessages(data.chats[0].messages);
            } else {
              fetchMessages(data.chats[0]._id);
            }
          }
        }
      } else {
        console.error('Error fetching chats:', data.message);
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  }, [initialCharacterId, initialChatId, fetchMessages]);

  // Funciones para manejar personajes (sin useCallback para evitar bucles)
  const initializeCharactersOnce = async () => {
    // Evitar ejecución múltiple
    if (charactersInitialized || charactersLoading) {
      console.log('⚠️ initializeCharactersOnce ya ejecutado o en progreso, saltando...');
      return;
    }

    try {
      setCharactersLoading(true);
      console.log('🚀 Iniciando carga de personajes...');

      // Obtener todos los personajes
      const response = await fetch(`${API_URL}/characters`, {
        credentials: 'include'
      });
      const data = await response.json();

      // Si no está autenticado, redirigir al login
      if (!data.success && data.message && data.message.includes('autenticación')) {
        console.log('❌ Usuario no autenticado, debe hacer login primero');
        return;
      }

      if (data.success) {
        let allCharacters = data.characters || [];

        // Seleccionar personaje basado en initialCharacterId si existe
        let characterToSelect: Character | null = null;

        if (initialCharacterId) {
          // Buscar el personaje específico del initialCharacterId en la lista cargada
          characterToSelect = allCharacters.find((char: Character) => char._id === initialCharacterId);

          if (!characterToSelect) {
            console.log('⚠️ Personaje no encontrado en lista principal, intentando fetch individual:', initialCharacterId);
            try {
              // Try to fetch specific character details (e.g. for public characters not in user's list)
              const docResponse = await fetch(`${API_URL}/characters/${initialCharacterId}`, {
                credentials: 'include'
              });
              const docData = await docResponse.json();
              if (docData.success && docData.character) {
                characterToSelect = docData.character;
                // Add to local list to prevent re-fetching issues
                allCharacters = [...allCharacters, docData.character];
                console.log('✅ Personaje individual cargado y agregado:', characterToSelect?.name);
              }
            } catch (err) {
              console.error('Error fetching individual char:', err);
            }
          } else {
            console.log('🎯 Personaje encontrado por initialCharacterId:', characterToSelect?.name);
          }
        }

        if (!characterToSelect && allCharacters.length > 0) {
          // Fallback: usar personaje por defecto o el primero
          characterToSelect = allCharacters.find((char: Character) => char.isDefault) || allCharacters[0];
          console.log('🎯 Usando personaje fallback:', characterToSelect?.name);
        }

        setCharacters(allCharacters);
        setSelectedCharacter(characterToSelect || null); // Ensure null if strictly no chars
        setCharactersInitialized(true);
        console.log('✅ Personajes inicializados correctamente');
      }
    } catch (error) {
      console.error('Error initializing characters:', error);
    } finally {
      setCharactersLoading(false);
    }
  };

  const selectCharacter = (character: Character) => {
    setSelectedCharacter(character);
    console.log('Personaje seleccionado:', character.name);

    // Buscar todos los chats de este personaje y obtener el más reciente
    const characterChats = chats.filter(chat => chat.characterId && chat.characterId._id === character._id);

    if (characterChats.length > 0) {
      // Ordenar chats por ID (más recientes primero, asumiendo que los IDs más recientes son lexicográficamente mayores)
      // En MongoDB, los ObjectIds contienen timestamp, por lo que ordenar por _id es efectivo
      const sortedChats = characterChats.sort((a, b) => b._id.localeCompare(a._id));
      const lastUsedChat = sortedChats[0];

      // Activar el último chat utilizado
      setActiveChat(lastUsedChat._id);
      fetchMessages(lastUsedChat._id);
      console.log(`Abriendo último chat utilizado con ${character.name}: ${lastUsedChat._id}`);
    } else {
      // Si no existe ningún chat, limpiar el chat activo y esperar a que el usuario cree uno
      setActiveChat(null);
      setMessages([]);
      console.log(`Personaje ${character.name} seleccionado - presiona '+Nuevo chat' para comenzar`);
    }
  };

  // useEffect para verificar sesión activa al cargar la página
  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        console.log('🔍 Verificando sesión activa al cargar la página...');
        const response = await fetch(`${API_URL}/chats`, {
          credentials: 'include'
        });

        if (response.ok) {
          console.log('✅ Sesión activa encontrada - restaurando estado');

          // Obtener información del usuario para restaurar el nombre y stats
          await fetchUserProfile();

          // Si la sesión es válida, restaurar el estado completo
          setIsLoggedIn(true);
          await initializeCharactersOnce();
          await fetchChats();
        } else {
          console.log('❌ No hay sesión activa - mantener estado de login');
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Error verificando sesión activa:', error);
        setIsLoggedIn(false);
      }
    };

    checkActiveSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar al montar el componente - sin dependencias

  // Effect to handle character switching when URL changes
  useEffect(() => {
    if (initialCharacterId) {
      console.log('🔄 Detected character change via URL:', initialCharacterId);
      // We need to re-fetch chats or at least re-evaluate the active chat
      // fetchChats handles the logic of selecting the right chat based on initialCharacterId prop (which it captures via closure or dependency)
      // HOWEVER, fetchChats captures the initialCharacterId value from its closure time if not in dependency.
      // We added initialCharacterId to fetchChats dependency, so fetchChats is recreated when it changes.
      // So simply calling it here works.
      fetchChats();

      // Also update selectedCharacter if possible?
      // initializeCharactersOnce checks "if charactersInitialized", so it might skip.
      // We might need a separate logic to update selectedCharacter if characters are already loaded.
      if (characters.length > 0) {
        const char = characters.find(c => c._id === initialCharacterId);
        if (char) {
          setSelectedCharacter(char);
        }
      }
    }
  }, [initialCharacterId, fetchChats, characters]);

  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight
    }
  }, [])

  // Función para el efecto de typing mejorado
  const typeMessage = useCallback((content: string, _messageIndex: number) => {
    console.log('🖊️ Iniciando efecto typing mejorado para mensaje:', content.substring(0, 50) + '...')

    // Si el efecto está deshabilitado, mostrar inmediatamente
    if (disableTypingEffect) {
      setMessages(prev => {
        const updated = [...prev]
        // Always update the last message (assistant's response)
        const lastIndex = updated.length - 1;
        if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: content
          }
        }
        return updated
      })
      setIsAssistantTyping(false)
      setTypingContent('')
      setFullResponseContent('')
      return
    }

    setFullResponseContent(content)
    setTypingContent('')
    setIsAssistantTyping(true)

    let currentIndex = 0
    const typingSpeed = 15 // ⚡ MÁS RÁPIDO: reducido de 30ms a 15ms

    const typeNextChar = () => {
      if (currentIndex < content.length) {
        // 🎨 FORMATEO EN TIEMPO REAL: aplicar formatMessage mientras se escribe
        const currentText = content.substring(0, currentIndex + 1)
        setTypingContent(currentText)
        currentIndex++
        setTimeout(typeNextChar, typingSpeed)
      } else {
        // Typing completado
        console.log('✅ Efecto typing completado')
        setIsAssistantTyping(false)
        setTypingContent('')
        setFullResponseContent('')

        // Actualizar el mensaje final en el estado - siempre el último mensaje assistant
        setMessages(prev => {
          const updated = [...prev]
          const lastIndex = updated.length - 1;
          if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
            updated[lastIndex] = {
              ...updated[lastIndex],
              content: content
            }
          }
          return updated
        })
      }
    }

    // Iniciar el typing después de un pequeño delay
    setTimeout(typeNextChar, 200)
  }, [disableTypingEffect])

  const updateChatTitle = useCallback((chatId: string, content: string) => {
    setChats(prevChats => prevChats.map(chat =>
      chat._id === chatId
        ? { ...chat, title: content.split(' ').slice(0, 3).join(' ') + '...' }
        : chat
    ))
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('=== HANDLE SUBMIT CALLED ===')
    console.log('Input value:', input)
    console.log('Input trimmed:', input.trim())
    console.log('Active chat:', activeChat)
    console.log('Should return early?', input.trim() === '' || !activeChat)

    if (input.trim() === '' || !activeChat) {
      console.log('❌ Returning early - input empty or no active chat')
      return
    }

    // 🛡️ CHECK MESSAGE LIMIT BEFORE SENDING (includes boosters)
    const messagesUsed = userStats?.usage?.messages || 0;
    const messageLimit = userStats?.usage?.messageLimit || 0;
    const boosters = userStats?.usage?.boosterMessages || 0;
    const remainingDaily = messageLimit - messagesUsed;
    const totalAvailable = Math.max(0, remainingDaily) + boosters;

    console.log('📊 Message limit check:', { messagesUsed, messageLimit, remainingDaily, boosters, totalAvailable });

    if (totalAvailable <= 0) {
      console.log('⚠️ No messages available (daily + boosters) - blocking send');
      setShowLimitModal(true);
      return; // Don't send, don't show message
    }

    const newMessage: Message = { role: 'user', content: input }

    // 🎆 [NUEVO] Crear burbuja del asistente inmediatamente con animación de carga
    const loadingMessage: Message = { role: 'assistant', content: '' }
    setMessages(prev => [...prev, newMessage, loadingMessage])
    setIsAssistantTyping(true)
    setTypingContent('') // Limpiar contenido de typing
    setInput('')

    console.log('💬 Burbuja del asistente creada con animación de carga')

    try {
      // Save the user message to the database
      await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatId: activeChat,
          message: newMessage
        }),
        credentials: 'include'
      });

      // Fetch all messages for the current chat
      const messagesResponse = await fetch(`${API_URL}/messages/${activeChat}`, {
        credentials: 'include'
      });
      const messagesData = await messagesResponse.json();
      const allMessages = messagesData.messages;

      console.log('All messages from DB:', allMessages);
      console.log('Selected character:', selectedCharacter?.name);

      // INJECTION LOGIC START
      let messagesToSend = [...allMessages];

      if (userPersonaEnabled && activeUserPersona) {
        console.log('👤 Injecting User Persona:', activeUserPersona.title);
        const systemIndex = messagesToSend.findIndex((m: any) => m.role === 'system');

        const personaMessage = {
          role: 'system',
          content: `[User Info: ${activeUserPersona.title}]\n${activeUserPersona.content}`
        };

        if (systemIndex !== -1) {
          // Inject after existing system prompt
          messagesToSend.splice(systemIndex + 1, 0, personaMessage as any);
        } else {
          // Inject at start
          messagesToSend.unshift(personaMessage as any);
        }
      }
      // INJECTION LOGIC END

      console.log('📤 Messages sent to API:', messagesToSend);
      console.log('🎯 Character ID:', selectedCharacter?._id);

      // Send the complete conversation to our backend AI proxy
      // System prompt is now stored as first message in DB
      // Backend will re-inject it if truncated
      const botResponse = await fetch(`${API_URL}/ai/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: messagesToSend,  // Modified messages array
          characterId: selectedCharacter?._id,  // For re-injection if truncated
          temperature: 0.7,
          max_tokens: 1000,
          top_p: 0.9
        }),
        credentials: 'include'
      });

      const botData = await botResponse.json();
      console.log('Bot response:', botData);

      // Check if message limit reached
      if (botResponse.status === 403 && botData.error === 'LIMIT_REACHED') {
        console.log('⚠️ Message limit reached');
        setShowLimitModal(true);
        // Remove the loading message
        setMessages(prev => prev.slice(0, -1));
        setIsAssistantTyping(false);
        return;
      }

      // Check for other errors
      if (!botResponse.ok) {
        throw new Error(botData.message || 'API error');
      }

      const botMessage: Message = { role: 'assistant', content: botData.choices[0].message.content };

      // Save the bot's response to the database
      await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatId: activeChat,
          message: botMessage
        }),
        credentials: 'include'
      });

      // 🖊️ [NUEVO] Iniciar efecto typing en lugar de agregar mensaje directamente
      const currentMessageIndex = messages.length + 1; // +1 porque ya agregamos user message y burbuja vacía
      console.log('🖊️ Iniciando efecto typing para índice:', currentMessageIndex);
      typeMessage(botMessage.content, currentMessageIndex);

      // 🚀 [SYNC FIX] Limpiar versiones de mensajes antiguos en el frontend
      // para sincronizar con la limpieza automática del backend
      console.log('🧹 [SYNC] Limpiando versiones de mensajes antiguos en el frontend...');

      const updatedVersions: { [messageIndex: number]: string[] } = {}
      const updatedIndices: { [messageIndex: number]: number } = {}

      // Solo mantener versiones para el último mensaje del asistente (que será el recién agregado)
      // Todos los demás mensajes deben perder sus versiones múltiples
      Object.keys(messageVersions).forEach(key => {
        const messageIndex = parseInt(key)
        console.log(`🧹 [SYNC] Limpiando versiones del mensaje ${messageIndex} (ya no es el último)`);
      })

      // Actualizar estado para remover sliders de mensajes antiguos
      setMessageVersions(updatedVersions)
      setCurrentVersionIndex(updatedIndices)

      console.log('✅ [SYNC] Estado de versiones sincronizado - sliders removidos de mensajes antiguos');

      // 🔄 Refresh user profile to update message counter in sidebar (backend already tracked usage)
      fetchUserProfile();

      if (messages.length === 0) {
        updateChatTitle(activeChat, botMessage.content);
      }
    } catch (error) {
      console.error('Error al conectar con la API:', error)
      // Replace loading bubble with error message that includes retry capability
      setMessages(prev => {
        const messagesWithoutLoading = prev.slice(0, -1); // Remove loading bubble
        return [...messagesWithoutLoading, {
          role: 'assistant',
          content: '__ERROR__There was an error processing your message. Change model and click Retry, or send a new message.'
        }];
      });
      setIsAssistantTyping(false);
      setTypingContent('');
    }
  }

  // Retry the last message after an error
  const retryLastMessage = async () => {
    if (!activeChat) return;

    console.log('🔄 Retrying last message...');

    // Remove the error message from UI
    setMessages(prev => prev.filter(m => !m.content.startsWith('__ERROR__')));

    // Add loading bubble
    const loadingMessage: Message = { role: 'assistant', content: '' };
    setMessages(prev => [...prev, loadingMessage]);
    setIsAssistantTyping(true);
    setTypingContent('');

    try {
      // Fetch messages from DB (without the error message)
      const messagesResponse = await fetch(`${API_URL}/messages/${activeChat}`, {
        credentials: 'include'
      });
      const messagesData = await messagesResponse.json();
      const allMessages = messagesData.messages;

      console.log('📤 Retrying with messages from DB:', allMessages.length);

      // Send to API
      const botResponse = await fetch(`${API_URL}/ai/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: selectedModel,
          messages: allMessages,
          characterId: selectedCharacter?._id,
          temperature: 0.7,
          max_tokens: 1000,
          top_p: 0.9
        }),
        credentials: 'include'
      });

      const botData = await botResponse.json();

      if (!botResponse.ok) {
        throw new Error(botData.message || 'API error');
      }

      const botMessage: Message = { role: 'assistant', content: botData.choices[0].message.content };

      // Save to DB
      await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: activeChat, message: botMessage }),
        credentials: 'include'
      });

      // Start typing effect
      typeMessage(botMessage.content, 0);
      fetchUserProfile();

    } catch (error) {
      console.error('Error retrying message:', error);
      setMessages(prev => {
        const messagesWithoutLoading = prev.slice(0, -1);
        return [...messagesWithoutLoading, {
          role: 'assistant',
          content: '__ERROR__There was an error processing your message. Change model and click Retry, or send a new message.'
        }];
      });
      setIsAssistantTyping(false);
      setTypingContent('');
    }
  };

  const startNewChat = async (characterId?: string) => {
    console.log('🚀 startNewChat ejecutado con characterId:', characterId);
    try {
      // Si no se proporciona characterId, usar el personaje seleccionado actualmente
      const charId = characterId || selectedCharacter?._id;
      console.log('🚀 charId final:', charId);
      console.log('🚀 selectedCharacter?._id:', selectedCharacter?._id);

      if (!charId) {
        alert('Please select a character before creating a new chat.');
        console.error('❌ No se puede crear chat sin un personaje seleccionado');
        return;
      }

      console.log('🚀 Enviando petición POST a /api/chats con characterId:', charId);
      const response = await fetch(`${API_URL}/chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ characterId: charId }),
        credentials: 'include'
      });

      console.log('🚀 Respuesta recibida, status:', response.status);
      const data = await response.json();
      console.log('🚀 Data de respuesta:', data);

      if (data.success) {
        console.log('✅ Chat creado exitosamente:', data.chat);
        setChats(prev => [...prev, data.chat]);
        setActiveChat(data.chat._id);
        setMessages([]);
      } else {
        console.error('❌ Error creating chat:', data.message);
      }
    } catch (error) {
      console.error('❌ Error creating new chat:', error);
    }
  }

  // Wrapper for the new chat button click
  const handleNewChatClick = () => {
    startNewChat();
  };

  const regenerateLastResponse = async () => {
    if (!activeChat || messages.length === 0) {
      console.log('❌ No hay chat activo o mensajes para regenerar');
      return;
    }

    // Encontrar el último mensaje del asistente
    let lastAssistantIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        lastAssistantIndex = i;
        break;
      }
    }
    if (lastAssistantIndex === -1) {
      console.log('❌ No se encontró mensaje del asistente para regenerar');
      return;
    }

    console.log('🔄 Regenerando última respuesta...');
    console.log('🔍 Índice del último mensaje del asistente:', lastAssistantIndex);

    // Marcar como regenerando para mostrar la animación
    setIsRegenerating(true);

    // Clear the message content immediately to show loading animation
    setMessages(prev => {
      const updated = [...prev];
      if (updated[lastAssistantIndex]) {
        updated[lastAssistantIndex] = { ...updated[lastAssistantIndex], content: '' };
      }
      return updated;
    });

    try {
      // ✅ [BUG FIX] NO eliminar el mensaje - solo obtener contexto sin él para generar nueva versión
      console.log('🔄 [FIXED] Regenerando sin eliminar mensaje - preservando historial de versiones');
      console.log('📊 [FIXED] Manteniendo todas las versiones existentes del mensaje en índice:', lastAssistantIndex);

      // NO removemos el mensaje del frontend, solo obtenemos el contexto sin él
      const messagesWithoutLast = messages.slice(0, lastAssistantIndex);

      console.log('📤 Usando mensajes del estado frontend para contexto (', messagesWithoutLast.length, 'mensajes)');
      console.log('📋 Último mensaje para contexto:', messagesWithoutLast[messagesWithoutLast.length - 1]);

      // Usar directamente los mensajes sin el último del asistente
      const allMessages = messagesWithoutLast;

      // Preparar mensajes con el system prompt del personaje seleccionado
      const systemMessage = selectedCharacter ? {
        role: 'system' as const,
        content: selectedCharacter.systemPrompt
      } : null;

      // 🎲 FORZAR VARIABILIDAD: Agregar instrucción específica para regeneración
      const variabilityInstruction = {
        role: 'system' as const,
        content: `[REGENERACIÓN FORZADA] Tu tarea es dar una respuesta COMPLETAMENTE DIFERENTE a cualquier respuesta anterior. Debes cambiar:
- El enfoque y perspectiva de tu respuesta
- El tono emocional y estilo de comunicación
- Las palabras, frases y expresiones específicas
- La estructura y organización de tu respuesta
- Los aspectos que decides destacar o ignorar
- El nivel de detalle y los ejemplos que usas
Sé muy creativo, original y atévete a tomar direcciones inesperadas. NO repitas patrones, frases o estructuras anteriores.`
      };

      const messagesWithSystem = systemMessage ?
        [systemMessage, variabilityInstruction, ...allMessages] :
        [variabilityInstruction, ...allMessages];

      console.log('🤖 Regenerando con system prompt + instrucción de variabilidad');
      console.log('🎲 Instrucción de variabilidad agregada');
      console.log('📋 Mensajes completos enviados a la API:', messagesWithSystem);

      // Añadir una pequeña pausa para evitar rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generar nueva respuesta con OpenRouter (Mistral AI)
      // Generar nueva respuesta con Backend Proxy (Mistral AI)
      const botResponse = await fetch(`${API_URL}/ai/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: messagesWithSystem,
          temperature: 1.1, // 🔥 MÁXIMA CREATIVIDAD: Muy alta para forzar diferencias drásticas
          max_tokens: 800,
          top_p: 0.95, // 🔥 DIVERSIDAD EXTREMA: Casi todos los tokens posibles
          top_k: 40, // 🔥 LIMITAR OPCIONES: Solo los 40 tokens más probables (más variabilidad)
          presence_penalty: 0.6, // 🔥 ANTI-REPETICIÓN FUERTE: Castiga fuertemente repetir contenido
          frequency_penalty: 0.4, // 🔥 VARIABILIDAD EXTREMA: Evita palabras ya usadas
          seed: Math.floor(Math.random() * 1000000) // 🎲 ALEATORIEDAD TOTAL: Seed diferente cada vez
        }),
        credentials: 'include'
      });

      if (!botResponse.ok) {
        console.error('❌ Error HTTP de OpenRouter:', botResponse.status, botResponse.statusText);
        throw new Error(`Error HTTP: ${botResponse.status}`);
      }

      const botData = await botResponse.json();
      console.log('🔄 Nueva respuesta generada:', botData);
      console.log('📊 Usage stats:', botData.usage);

      // Validar que la respuesta tenga contenido
      const responseContent = botData.choices?.[0]?.message?.content;
      if (!responseContent || responseContent.trim() === '') {
        console.error('❌ Respuesta vacía de la API');
        console.log('🔍 Respuesta completa:', botData.choices?.[0]?.message);
        throw new Error('La API devolvió una respuesta vacía');
      }

      const newBotMessage: Message = { role: 'assistant', content: responseContent.trim() };
      console.log('✅ [FIXED] Nueva versión validada (no es mensaje nuevo):', newBotMessage);

      // 🔥 [BUG FIX] NO guardar como mensaje nuevo - agregar directamente como versión al mensaje existente
      console.log('🔄 [FIXED] Saltando guardado de mensaje nuevo - agregando versión directamente');
      console.log('📊 [FIXED] Agregando versión al mensaje existente en índice:', lastAssistantIndex);

      // Agregar nueva versión usando el backend (este es el único save necesario)
      const versionResponse = await fetch(`${API_URL}/messages/${activeChat}/${lastAssistantIndex}/version`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: newBotMessage.content
        }),
        credentials: 'include'
      });

      const versionData = await versionResponse.json();
      console.log('📊 Resultado de agregar versión:', versionData);

      if (versionData.success) {
        console.log('📊 [BUG FIX] Datos de versiones del backend:', versionData);
        console.log('📊 [BUG FIX] Versiones recibidas:', versionData.versions?.length || 0);
        console.log('📊 [BUG FIX] Índice actual recibido:', versionData.currentVersionIndex);

        // Actualizar estado local con datos del backend
        setMessageVersions(prev => {
          const newVersions = {
            ...prev,
            [lastAssistantIndex]: versionData.versions.map((v: { content: string }) => v.content)
          };
          console.log('📊 [BUG FIX] Estado de versiones actualizado:', newVersions);
          return newVersions;
        });

        setCurrentVersionIndex(prev => {
          const newIndices = {
            ...prev,
            [lastAssistantIndex]: versionData.currentVersionIndex
          };
          console.log('📊 [BUG FIX] Índices actualizados:', newIndices);
          return newIndices;
        });

        // Actualizar el mensaje existente con la nueva versión
        setMessages(prev => {
          const updated = [...prev];
          updated[lastAssistantIndex] = {
            ...updated[lastAssistantIndex],
            content: newBotMessage.content
          };
          console.log('📊 [BUG FIX] Mensaje actualizado en índice', lastAssistantIndex);
          return updated;
        });
      } else {
        console.error('❌ Error al agregar versión al backend:', versionData.message);
        throw new Error('Error agregando versión a la base de datos');
      }

      console.log('✅ Respuesta regenerada exitosamente');

      // 🔄 Refresh user profile to update message counter in sidebar
      fetchUserProfile();

    } catch (error) {
      console.error('❌ Error regenerando respuesta:', error);
      alert('Error regenerating response. Please try again.');
      // Reload messages from DB to ensure sync
      if (activeChat) {
        fetchMessages(activeChat);
      }
    } finally {
      // Siempre desactivar el estado de regeneración
      setIsRegenerating(false);
    }
  }



  // Filtrar chats por personaje seleccionado
  const getFilteredChats = () => {
    let filtered = [];
    if (!selectedCharacter) {
      filtered = chats.filter(chat => !chat.characterId);
    } else {
      filtered = chats.filter(chat => chat.characterId && chat.characterId._id === selectedCharacter._id);
    }
    // Sort by updatedAt descending
    return filtered.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      return dateB - dateA;
    });
  }

  const deleteChat = async (chatId: string) => {
    try {
      const response = await fetch(`${API_URL}/chats/${chatId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setChats(prev => prev.filter(chat => chat._id !== chatId));
        if (activeChat === chatId) {
          setActiveChat(null);
          setMessages([]);
        }
      } else {
        console.error('Error deleting chat:', data.message);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  }

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 60
      const currentScroll = carouselRef.current.scrollLeft
      const maxScroll = carouselRef.current.scrollWidth - carouselRef.current.clientWidth

      if (direction === 'left') {
        const newScroll = Math.max(currentScroll - scrollAmount * 5, 0)
        carouselRef.current.scrollTo({ left: newScroll, behavior: 'smooth' })
      } else {
        const newScroll = Math.min(currentScroll + scrollAmount * 5, maxScroll)
        carouselRef.current.scrollTo({ left: newScroll, behavior: 'smooth' })
      }
    }
  }

  const formatMessage = (content: string, role: 'user' | 'assistant') => {
    // Detectar texto entre asteriscos para acciones de roleplay
    const parts = content.split(/(\*[^*]+\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        // Acciones entre asteriscos - mismo color para ambos roles
        return (
          <span key={index} style={{
            color: 'rgb(176, 216, 251)',
            fontStyle: 'italic'
          }}>
            {part}
          </span>
        );
      } else {
        // Diálogo normal - blanco
        return (
          <span key={index} style={{ color: 'white' }}>
            {part}
          </span>
        );
      }
    });
  }

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((e) => {
        console.error(`Error attempting to enable full-screen mode: ${e.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        scrollCarousel('left')
      } else if (event.key === 'ArrowRight') {
        scrollCarousel('right')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Track previous messages length to detect new messages
  const prevMessagesLengthRef = useRef(messages.length);

  // Smart scroll: only auto-scroll when near bottom OR when a new message is added
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const messagesLengthChanged = messages.length !== prevMessagesLengthRef.current;
    const isMessagesAdded = messages.length > prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;

    // Check if user is near the bottom (within 150px)
    const isNearBottom = scrollArea.scrollHeight - scrollArea.scrollTop - scrollArea.clientHeight < 150;

    // Only scroll to bottom if:
    // 1. A new message was added (user sent or received new message), OR
    // 2. User is already near the bottom
    if (isMessagesAdded || isNearBottom) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom])

  useEffect(() => {
    // Si ya estamos logueados (desde el AuthWrapper), no verificar de nuevo
    if (isAlreadyLoggedIn) {
      initializeCharactersOnce();
      fetchChats();
      return;
    }

    // Check if the user is already logged in
    fetch(`${API_URL}/chats`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setIsLoggedIn(true);
          fetchChats();
        }
      })
      .catch(error => console.error('Error checking authentication:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchChats, isAlreadyLoggedIn]);

  // const toggleInfoPanel = () => {
  //   setShowInfoPanel(!showInfoPanel)
  // }

  const toggleGallery = () => {
    setShowGallery(!showGallery)
  }



  // Funciones para edición de mensajes
  const startEditingMessage = (index: number, content: string) => {
    setEditingMessageIndex(index)
    setEditingContent(content)
  }

  const cancelEditing = () => {
    setEditingMessageIndex(null)
    setEditingContent('')
  }

  const saveEditedMessage = async () => {
    if (!activeChat || editingMessageIndex === null || !editingContent.trim()) {
      return
    }

    console.log('🔍 Debug - saveEditedMessage:', {
      activeChat,
      editingMessageIndex,
      editingContent: editingContent.substring(0, 50)
    })

    try {
      const url = `/api/messages/${activeChat}/${editingMessageIndex}`
      console.log('🔍 Debug - URL construida:', url)

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: editingContent.trim() }),
        credentials: 'include'
      })

      const data = await response.json()

      if (data.success) {
        // Actualizar el mensaje en el estado local
        const updatedMessages = [...messages]
        updatedMessages[editingMessageIndex] = {
          ...updatedMessages[editingMessageIndex],
          content: editingContent.trim(),
          timestamp: new Date(data.updatedMessage.timestamp)
        }
        setMessages(updatedMessages)

        // Limpiar estado de edición
        cancelEditing()

        console.log('✏️ Message edited successfully')
      } else {
        console.error('Error editing message:', data.message)
        alert('Error editing message: ' + data.message)
      }
    } catch (error) {
      console.error('Error editing message:', error)
      alert('Connection error while editing message')
    }
  }

  // Función para eliminar mensaje
  const deleteMessage = async (index: number) => {
    if (!activeChat) {
      return
    }

    setDeletingMessageIndex(index)

    try {
      const response = await fetch(`/api/messages/${activeChat}/${index}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      const data = await response.json()

      if (data.success) {
        // Actualizar el estado local eliminando el mensaje
        const updatedMessages = messages.filter((_, i) => i !== index)
        setMessages(updatedMessages)

        // Si estaba editando este mensaje, cancelar la edición
        if (editingMessageIndex === index) {
          cancelEditing()
        }

        console.log('🗑️ Message deleted successfully')
      } else {
        console.error('Error deleting message:', data.message)
        alert('Error deleting message: ' + data.message)
      }
    } catch (error) {
      console.error('Error deleting message:', error)
      alert('Connection error while deleting message')
    } finally {
      setDeletingMessageIndex(null)
    }
  }

  // Funciones para navegación de versiones regeneradas
  const navigateToVersion = async (messageIndex: number, versionIndex: number) => {
    const versions = messageVersions[messageIndex];
    if (!versions || versionIndex < 0 || versionIndex >= versions.length) {
      return;
    }

    try {
      // Usar el backend para cambiar la versión
      const response = await fetch(`${API_URL}/messages/${activeChat}/${messageIndex}/version/${versionIndex}`, {
        method: 'PUT',
        credentials: 'include'
      });

      const data = await response.json();
      console.log('🔄 [BUG FIX] Navegación exitosa - backend responde:', data);
      console.log('🔄 [BUG FIX] Nuevo contenido del backend:', data.content?.substring(0, 100) + '...');
      console.log('🔄 [BUG FIX] Nuevo índice de versión:', data.currentVersionIndex);

      if (data.success) {
        // Actualizar el índice de la versión actual
        setCurrentVersionIndex(prev => {
          const newIndex = {
            ...prev,
            [messageIndex]: data.currentVersionIndex
          };
          console.log('🔄 [BUG FIX] Índice de versión actualizado:', newIndex);
          return newIndex;
        });

        // CRUCIAL: Actualizar el contenido del mensaje de manera forzada
        setMessages(prev => {
          const updated = [...prev];
          console.log('🔄 [BUG FIX] Mensaje ANTES del cambio:', updated[messageIndex]);

          // Actualizar tanto el contenido como forzar re-render
          updated[messageIndex] = {
            ...updated[messageIndex],
            content: data.content,
            timestamp: new Date() // Forzar cambio para triggear re-render
          };

          console.log('🔄 [BUG FIX] Mensaje DESPUÉS del cambio:', updated[messageIndex]);
          console.log('🔄 [BUG FIX] Verificación - contenido cambiado:',
            updated[messageIndex].content.substring(0, 50) + '...');

          return updated;
        });

        console.log('✅ [BUG FIX] Navegación de versión completada con logs debug');
      } else {
        console.error('❌ Error al cambiar versión:', data.message);
      }
    } catch (error) {
      console.error('❌ Error al navegar a versión:', error);
    }
  }

  const navigatePreviousVersion = (messageIndex: number) => {
    const currentIndex = currentVersionIndex[messageIndex] || 0;
    if (currentIndex > 0) {
      navigateToVersion(messageIndex, currentIndex - 1);
    }
  }

  const navigateNextVersion = (messageIndex: number) => {
    const versions = messageVersions[messageIndex];
    const currentIndex = currentVersionIndex[messageIndex] || 0;
    if (versions && currentIndex < versions.length - 1) {
      navigateToVersion(messageIndex, currentIndex + 1);
    }
  }

  const getVersionInfo = (messageIndex: number) => {
    const versions = messageVersions[messageIndex];
    const currentIndex = currentVersionIndex[messageIndex] || 0;
    if (!versions || versions.length <= 1) {
      return null;
    }
    return {
      current: currentIndex + 1,
      total: versions.length,
      hasMultiple: versions.length > 1
    };
  }
  // Mostrar loading si estamos cargando personajes cuando ya estamos logueados
  if (isAlreadyLoggedIn && charactersLoading && !charactersInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #533483 75%, #f093fb 100%)',
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          fontSize: '18px',
          fontWeight: '500'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '4px solid rgba(255,107,107,0.3)',
            borderTop: '4px solid #ff6b6b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading characters...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Legacy login screen removed.
  // Auth is now handled via TopBar login modal in the Lobby.
  // If user reaches here without login, the chat functionality might be limited.

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100dvh',
      overflow: isMobile ? 'hidden' : 'visible',
      background: isDarkMode ? 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)' : '#ffffff',
      color: isDarkMode ? 'white' : '#1e293b',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      transition: 'background 0.3s, color 0.3s'
    }}>
      <style>
        {`
          @keyframes fadeInUp {
            from {
            opacity: 0;
          transform: translateY(20px);
            }
          to {
            opacity: 1;
          transform: translateY(0);
            }
          }

          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(255, 107, 107, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 107, 107, 0);
            }
          }

          @keyframes slideIn {
            from {
            opacity: 0;
          transform: translateX(-20px);
            }
          to {
            opacity: 1;
          transform: translateX(0);
            }
          }

          @keyframes fadeIn {
            from {
            opacity: 0;
            }
          to {
            opacity: 1;
            }
          }

          @keyframes modalFadeIn {
            from {
            opacity: 0;
          backdrop-filter: blur(0px);
            }
          to {
            opacity: 1;
          backdrop-filter: blur(8px);
            }
          }

          @keyframes modalSlideUp {
            from {
            opacity: 0;
          transform: translateY(50px) scale(0.95); 
            }
          to {
            opacity: 1;
          transform: translateY(0) scale(1); 
            }
          }

          ::-webkit-scrollbar {
            width: 8px;
          height: 8px;
          }

          ::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #ff6b6b, #ff4757);
          border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #ff8585, #ff6b6b);
          }
        `}
      </style>


      <div style={{
        display: 'flex',
        flex: '1',
        overflow: 'hidden',
        position: 'relative'
      }}>

        <div
          style={{
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            backgroundColor: '#000', // Fallback color
            isolation: 'isolate' // Create a new stacking context
          }}
        >
          {/* 1. Fondo borroso que llena toda la pantalla (Ambiente) */}
          {/* 1. Fondo borroso suave */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${selectedCharacter?.backgroundImage || backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(30px) brightness(0.5)',
            transform: 'scale(1.2)',
            zIndex: -2
          }} />

          {/* 2. Imagen principal con settings dinámicos */}
          {(() => {
            // Get device-specific settings
            const bgSettings = isMobile
              ? selectedCharacter?.backgroundSettings?.mobile
              : selectedCharacter?.backgroundSettings?.desktop;

            const posX = bgSettings?.positionX ?? 50;
            const posY = bgSettings?.positionY ?? 50;
            const zoom = bgSettings?.zoom ?? 100;

            // En móvil: usar cover como base, zoom como escala adicional
            // En desktop: usar el porcentaje directo para más control
            const bgSize = isMobile ? 'cover' : `${zoom}%`;
            const scaleValue = isMobile ? zoom / 100 : 1;

            return (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${selectedCharacter?.backgroundImage || backgroundImage})`,
                backgroundSize: bgSize,
                backgroundPosition: `${posX}% ${posY}%`,
                backgroundRepeat: 'no-repeat',
                width: '100%',
                height: '100%',
                transform: isMobile && zoom !== 100 ? `scale(${scaleValue})` : 'none',
                transformOrigin: `${posX}% ${posY}%`,
                zIndex: -1
              }} />
            );
          })()}

          {/* 3. Capa de oscurecimiento opcional controlada por configuración */}
          <div style={{
            position: 'absolute',
            inset: '0',
            backgroundColor: backgroundBlurEnabled ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)',
            backdropFilter: backgroundBlurEnabled ? 'blur(3px)' : 'none', // Add subtle extra blur if enabled
            zIndex: 0
          }} />

          {/* Vignette effect para crear intimidad */}
          <div style={{
            position: 'absolute',
            inset: '0',
            background: 'radial-gradient(ellipse at center, transparent 0%, transparent 60%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0.6) 100%)',
            pointerEvents: 'none',
            zIndex: 5
          }}></div>

          {/* Header Minimalista/Invisible - Solo elementos flotantes */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 20px', // Padding mínimo
            background: 'transparent', // SIN FONDO
            position: 'relative',
            minHeight: '60px', // Altura mínima para los elementos
            // Sin bordes, sombras ni efectos - completamente invisible
            zIndex: '10' // Asegurar que flote sobre el fondo
          }}>
            {/* ⬅️ Botón de Menú (Hamburger) */}
            <button
              onClick={() => isMobile ? setIsMobileSidebarOpen(true) : setShowNavigationSidebar(true)}
              style={{
                position: 'absolute',
                left: '20px',
                background: 'rgba(107,191,255,0.2)',
                border: '1px solid rgba(107,191,255,0.3)',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                color: '#6bbfff',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(107,191,255,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(107,191,255,0.2)';
              }}
              title="Menu"
            >
              <Menu size={20} />
            </button>

            {/* ⚙️ Botón de Configuración LLM (Desktop only - moved to right on mobile) */}
            {!isMobile && (
              <button
                onClick={() => setShowLLMModal(true)}
                style={{
                  position: 'absolute',
                  left: '575px',
                  background: 'rgba(107,191,255,0.2)',
                  border: '1px solid rgba(107,191,255,0.3)',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  color: '#6bbfff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  zIndex: 20
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(107,191,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(107,191,255,0.2)';
                }}
                title="Configuración de LLM"
              >
                <Settings size={20} />
              </button>
            )}




            {/* 🎯 DATOS DEL PERSONAJE CENTRADOS */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              transform: isMobile ? 'none' : 'translateX(-25px)' // Only offset on desktop when avatar is shown
            }}>

              {!isMobile && (
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid rgba(255,107,107,0.3)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  <img
                    src={selectedCharacter?.avatar || backgroundImage}
                    alt={selectedCharacter?.name || "Profile"}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              )}
              <div style={{ textAlign: 'center' }}> {/* 🎯 CENTRAR TEXTO */}
                <div style={{
                  fontWeight: '600',
                  fontSize: '16px',
                  marginBottom: '4px'
                }}>
                  {selectedCharacter?.name || "Selecciona un personaje"}
                </div>
              </div>
            </div>

            {/* 🎯 RIGHT SIDE CONTROLS */}
            <div style={{
              position: 'absolute',
              right: isMobile ? '12px' : '20px',
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              {/* LLM Selector - visible on mobile */}
              {isMobile && (
                <button
                  onClick={() => setShowLLMModal(true)}
                  style={{
                    background: 'rgba(107, 191, 255, 0.25)',
                    border: '1px solid rgba(107, 191, 255, 0.5)',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    cursor: 'pointer',
                    color: '#6bbfff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Select AI Model"
                >
                  <Settings size={18} />
                </button>
              )}

              {/* Fullscreen - desktop only */}
              {!isMobile && (
                <span style={{ cursor: 'pointer' }} onClick={toggleFullScreen}>
                  <Maximize2 size={20} />
                </span>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex',
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            // On mobile, use column layout to push messages to bottom
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'flex-end' : 'flex-start'
          }}>
            <div
              ref={scrollAreaRef}
              style={{
                flexGrow: 1,
                flexShrink: 1,
                padding: isMobile ? '12px' : '20px',
                paddingBottom: isMobile ? '100px' : '20px', // Extra space for fixed input on mobile
                zIndex: '10',
                position: 'relative',
                overflowY: 'auto',
                // Mobile: max 55% of screen height for messages (unless full screen is enabled)
                maxHeight: isMobile
                  ? (fullScreenChatEnabled ? 'calc(100dvh - 140px)' : '55dvh')
                  : 'calc(100dvh - 200px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                // Fade effect at top where messages get cut off
                maskImage: isMobile ? 'linear-gradient(to bottom, transparent 0%, black 8%, black 100%)' : 'none',
                WebkitMaskImage: isMobile ? 'linear-gradient(to bottom, transparent 0%, black 8%, black 100%)' : 'none'
              }}
            >
              {messages.map((message, originalIndex) => message.role !== 'system' && (
                <div
                  key={originalIndex}
                  data-original-index={originalIndex}
                  style={{
                    marginBottom: isMobile ? '16px' : '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: isMobile ? '100%' : '800px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    width: '100%',
                    paddingLeft: isMobile ? (message.role === 'user' ? '15px' : '4px') : (message.role === 'user' ? '150px' : '20px'),
                    paddingRight: isMobile ? (message.role === 'user' ? '4px' : '4px') : (message.role === 'user' ? '30px' : '10px')
                  }}>

                    {/* Contenedor completo del mensaje */}
                    <div style={{
                      display: 'flex',
                      alignItems: message.role === 'assistant' ? 'flex-end' : 'flex-start',
                      gap: isMobile ? '6px' : '12px',
                      maxWidth: message.role === 'user' ? (isMobile ? '95%' : '350px') : (isMobile ? '100%' : '85%'),
                      width: message.role === 'user' ? 'auto' : '100%'
                    }}>

                      {/* Avatar del asistente (solo para mensajes del asistente) */}
                      {message.role === 'assistant' && (
                        <div style={{
                          width: isMobile ? '32px' : '40px',
                          height: isMobile ? '32px' : '40px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          <img
                            src={selectedCharacter?.avatar || backgroundImage}
                            alt={selectedCharacter?.name || "AI Assistant"}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                      )}

                      {/* Burbuja de mensaje con nombre interno */}
                      <div
                        style={{
                          background: message.role === 'user'
                            ? (isMobile ? 'rgba(255, 107, 107, 0.65)' : 'linear-gradient(135deg, #ff6b6b, #ff4757)')
                            : (isMobile ? 'rgba(42, 42, 62, 0.65)' : 'linear-gradient(135deg, #3a3a4e, #2a2a3e)'),
                          backdropFilter: isMobile ? 'blur(12px)' : 'none',
                          borderRadius: message.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          animation: 'fadeInUp 0.3s ease',
                          position: 'relative',
                          minWidth: isMobile ? '120px' : '200px',
                          // Para usuarios: expandir la burbuja hacia la izquierda (desktop only)
                          ...(message.role === 'user' && !isMobile && {
                            width: '500px',
                            marginLeft: '-250px'
                          }),
                          // Mobile users: fit content width
                          ...(message.role === 'user' && isMobile && {
                            maxWidth: '100%',
                            wordBreak: 'break-word'
                          })
                        }}
                      >
                        {/* Header con nombre y contador de versiones */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 16px 4px 16px',
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          position: 'relative'
                        }}>
                          {/* Nombre del personaje/usuario */}
                          <div style={{
                            fontSize: '11px',
                            color: 'rgba(255,255,255,0.6)',
                            fontWeight: '600'
                          }}>
                            {message.role === 'user' ? loggedUser : (selectedCharacter?.name || 'Assistant')}
                          </div>

                          {/* 🔍 Contador de versiones en la esquina superior derecha (solo para asistente) */}
                          {message.role === 'assistant' && getVersionInfo(originalIndex) && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px', // Reducido de 8px a 4px
                              padding: '2px 6px', // Reducido de 8px 12px
                              background: 'rgba(255,255,255,0.08)',
                              borderRadius: '8px', // Reducido de 12px
                              border: '1px solid rgba(255,255,255,0.15)',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.1)', // Reducido de 0 2px 8px
                              fontSize: '10px' // Reducido para que sea compacto
                            }}>
                              {/* Botón anterior */}
                              <button
                                onClick={() => navigatePreviousVersion(originalIndex)}
                                disabled={currentVersionIndex[originalIndex] <= 0}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: currentVersionIndex[originalIndex] <= 0 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
                                  cursor: currentVersionIndex[originalIndex] <= 0 ? 'not-allowed' : 'pointer',
                                  fontSize: '10px', // Reducido de 16px
                                  padding: '2px', // Reducido de 4px
                                  transition: 'all 0.2s ease',
                                  opacity: currentVersionIndex[originalIndex] <= 0 ? 0.5 : 1
                                }}
                                title="Previous version"
                              >
                                ◀
                              </button>

                              {/* Contador compacto */}
                              <div style={{
                                fontSize: '9px', // Reducido de 12px
                                color: 'rgba(255,255,255,0.8)',
                                fontWeight: '600',
                                minWidth: '28px', // Reducido de 40px
                                textAlign: 'center',
                                background: 'rgba(255,255,255,0.1)',
                                padding: '2px 4px', // Reducido de 4px 8px
                                borderRadius: '4px', // Reducido de 8px
                                border: '1px solid rgba(255,255,255,0.1)'
                              }}>
                                {getVersionInfo(originalIndex)?.current}/{getVersionInfo(originalIndex)?.total}
                              </div>

                              {/* Botón siguiente */}
                              <button
                                onClick={() => navigateNextVersion(originalIndex)}
                                disabled={currentVersionIndex[originalIndex] >= (messageVersions[originalIndex]?.length || 1) - 1}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: currentVersionIndex[originalIndex] >= (messageVersions[originalIndex]?.length || 1) - 1 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
                                  cursor: currentVersionIndex[originalIndex] >= (messageVersions[originalIndex]?.length || 1) - 1 ? 'not-allowed' : 'pointer',
                                  fontSize: '10px', // Reducido de 16px
                                  padding: '2px', // Reducido de 4px
                                  transition: 'all 0.2s ease',
                                  opacity: currentVersionIndex[originalIndex] >= (messageVersions[originalIndex]?.length || 1) - 1 ? 0.5 : 1
                                }}
                                title="Next version"
                              >
                                ▶
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Contenido del mensaje */}
                        <div style={{
                          padding: '5px 16px 8px 16px',
                          fontSize: '15px',
                          lineHeight: '1.5',
                          textAlign: 'left'
                        }}>
                          {/* 🎆 [NUEVO] Lógica mejorada de renderizado con efecto typing */}
                          {/* Caso 1: Regenerando y es el último mensaje del asistente */}
                          {isRegenerating && message.role === 'assistant' && originalIndex === messages.length - 1 ? (
                            <LoadingDots />
                          ) : /* Caso 2: Efecto typing activo en mensaje del asistente */
                            isAssistantTyping && message.role === 'assistant' && originalIndex === messages.length - 1 ? (
                              typingContent ? (
                                /* 🎨 FORMATEO EN TIEMPO REAL: aplicar formatMessage al contenido que se está escribiendo */
                                <div>{formatMessage(typingContent, 'assistant')}</div>
                              ) : (
                                <LoadingDots />
                              )
                            ) : /* Caso 3: Modo edición */
                              editingMessageIndex === originalIndex ? (
                                /* Modo edición */
                                <div>
                                  <textarea
                                    value={editingContent}
                                    onChange={(e) => setEditingContent(e.target.value)}
                                    style={{
                                      width: '100%',
                                      minHeight: '60px',
                                      background: 'rgba(255,255,255,0.1)',
                                      border: '1px solid rgba(255,255,255,0.2)',
                                      borderRadius: '8px',
                                      padding: '8px',
                                      color: 'white',
                                      fontSize: '15px',
                                      lineHeight: '1.5',
                                      resize: 'vertical',
                                      outline: 'none'
                                    }}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && e.ctrlKey) {
                                        saveEditedMessage()
                                      } else if (e.key === 'Escape') {
                                        cancelEditing()
                                      }
                                    }}
                                  />
                                  <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    marginTop: '8px',
                                    justifyContent: 'flex-end'
                                  }}>
                                    <button
                                      onClick={cancelEditing}
                                      style={{
                                        background: 'rgba(255,255,255,0.1)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '6px',
                                        padding: '4px 12px',
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.2)'
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.1)'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={saveEditedMessage}
                                      style={{
                                        background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '4px 12px',
                                        color: 'white',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, #ff8585, #ff6b6b)'
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, #ff6b6b, #ff4757)'
                                      }}
                                    >
                                      Save
                                    </button>
                                  </div>
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'rgba(255,255,255,0.5)',
                                    marginTop: '4px'
                                  }}>
                                    Ctrl+Enter to save, Esc to cancel
                                  </div>
                                </div>
                              ) : message.content.startsWith('__ERROR__') ? (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '12px',
                                  padding: '8px 0'
                                }}>
                                  <div style={{ color: '#ff6b6b' }}>
                                    ⚠️ {message.content.replace('__ERROR__', '')}
                                  </div>
                                  <button
                                    onClick={retryLastMessage}
                                    style={{
                                      background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '12px',
                                      padding: '10px 20px',
                                      cursor: 'pointer',
                                      fontWeight: '600',
                                      fontSize: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      width: 'fit-content'
                                    }}
                                  >
                                    🔄 Retry with current model
                                  </button>
                                </div>
                              ) : (
                                formatMessage(message.content, message.role as 'user' | 'assistant')
                              )}
                        </div>
                      </div>

                      {/* Botones de acción para cada mensaje */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile ? '8px' : '4px',
                        alignItems: 'center'
                      }}>
                        {/* Ícono de regenerar - ARRIBA del lápiz, solo en el último mensaje del asistente */}
                        {message.role === 'assistant' && originalIndex === messages.length - 1 && originalIndex > 0 && message.content !== selectedCharacter?.greeting && !isAssistantTyping && message.content !== '' && !message.content.startsWith('__ERROR__') && (
                          <div
                            style={{
                              cursor: 'pointer',
                              padding: isMobile ? '8px' : '6px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: isMobile ? '36px' : '28px',
                              height: isMobile ? '36px' : '28px',
                              fontSize: isMobile ? '16px' : '14px',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 8px rgba(168, 85, 247, 0.4)',
                              flexShrink: 0,
                              color: 'white'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'rotate(180deg) scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'rotate(0) scale(1)';
                            }}
                            onClick={regenerateLastResponse}
                            title="Regenerate response"
                          >
                            ⟳
                          </div>
                        )}

                        {/* Botón de editar (para todos los mensajes) */}
                        <div
                          style={{
                            cursor: 'pointer',
                            padding: isMobile ? '8px' : '6px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #4a4a5e, #3a3a4e)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: isMobile ? '36px' : '28px',
                            height: isMobile ? '36px' : '28px',
                            fontSize: isMobile ? '14px' : '12px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                            flexShrink: 0,
                            opacity: editingMessageIndex === originalIndex ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (editingMessageIndex !== originalIndex) {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #6bbfff, #4a90e2)';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (editingMessageIndex !== originalIndex) {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #4a4a5e, #3a3a4e)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }
                          }}
                          onClick={() => {
                            if (editingMessageIndex !== originalIndex) {
                              startEditingMessage(originalIndex, message.content)
                            }
                          }}
                          title="Edit message"
                        >
                          ✏️
                        </div>

                        {/* Botón de eliminar (para todos los mensajes) */}
                        <div
                          style={{
                            cursor: 'pointer',
                            padding: isMobile ? '8px' : '6px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #4a4a5e, #3a3a4e)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: isMobile ? '36px' : '28px',
                            height: isMobile ? '36px' : '28px',
                            fontSize: isMobile ? '14px' : '12px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                            flexShrink: 0,
                            opacity: deletingMessageIndex === originalIndex ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (deletingMessageIndex !== originalIndex) {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #ff6b6b, #ff4757)';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (deletingMessageIndex !== originalIndex) {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #4a4a5e, #3a3a4e)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }
                          }}
                          onClick={() => {
                            if (deletingMessageIndex !== originalIndex && editingMessageIndex !== originalIndex) {
                              deleteMessage(originalIndex)
                            }
                          }}
                          title="Delete message"
                        >
                          🗑️
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              ))
              }
            </div>

            {/* Info Panel */}
            {showInfoPanel && (
              <div style={{
                width: '300px',
                backgroundColor: '#1E1E1E',
                borderLeft: '1px solid #333',
                padding: '16px',
                overflowY: 'auto',
                position: 'absolute',
                right: '0',
                top: '0',
                bottom: '0',
                zIndex: '30',
              }}>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    margin: '0 auto 8px',
                  }}>
                    <img
                      src={backgroundImage}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.2em' }}>Na-Nox Amv Antez</div>
                  <div style={{ color: '#999' }}>Active 5 hours ago</div>
                </div>
                <div style={{
                  backgroundColor: '#333',
                  padding: '8px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <span>&#128274;</span>
                  <span>End-to-end encrypted</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '16px' }}>
                  <button style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '24px', marginBottom: '4px' }}>&#128100;</span>
                    <span>Profile</span>
                  </button>
                  <button style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '24px', marginBottom: '4px' }}>&#128276;</span>
                    <span>Mute</span>
                  </button>
                  <button style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '24px', marginBottom: '4px' }}>&#128269;</span>
                    <span>Search</span>
                  </button>
                </div>
                <div>
                  <div style={{ marginBottom: '8px', cursor: 'pointer' }}>
                    <span>Chat info</span>
                    <span style={{ float: 'right' }}>&#9660;</span>
                  </div>
                  <div style={{ marginBottom: '8px', cursor: 'pointer' }}>
                    <span>Customize chat</span>
                    <span style={{ float: 'right' }}>&#9660;</span>
                  </div>
                  <div
                    style={{ marginBottom: '8px', cursor: 'pointer' }}
                    onClick={toggleGallery}
                  >
                    <span>Media and files</span>
                    <span style={{ float: 'right' }}>{showGallery ? '▲' : '▼'}</span>
                  </div>
                  {showGallery && (
                    <div style={{ marginLeft: '16px', marginBottom: '16px' }}>
                      <div style={{ marginBottom: '8px' }}>&#128247; Media</div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '8px'
                      }}>
                        {galleryImages.map((src, index) => (
                          <img
                            key={index}
                            src={src}
                            alt={`Gallery image ${index + 1}`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: '4px',
                              objectFit: 'cover'
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ marginTop: '8px' }}>&#128196; Files</div>
                    </div>
                  )}
                  <div style={{ marginBottom: '8px', cursor: 'pointer' }}>
                    <span>Privacy and Help</span>
                    <span style={{ float: 'right' }}>&#9660;</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            style={{
              padding: isMobile ? '12px 16px' : '20px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              zIndex: '100',
              position: isMobile ? 'fixed' : 'relative',
              bottom: isMobile ? '0' : 'auto',
              left: isMobile ? '0' : 'auto',
              right: isMobile ? '0' : 'auto',
              background: 'linear-gradient(90deg, rgba(42,42,62,0.98) 0%, rgba(31,31,46,0.98) 100%)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 -2px 10px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{
              display: 'flex',
              gap: '8px',
              maxWidth: isMobile ? '100%' : '800px',
              margin: '0 auto',
              width: '100%',
              alignItems: 'center'
            }}>
              {/* 🎯 BOTÓN DEL MENÚ MOVIDO AQUÍ */}
              <button
                onClick={() => setShowChatsModal(!showChatsModal)}
                type="button" // 🔒 IMPORTANTE: Evitar que dispare el submit del form
                style={{
                  background: 'rgba(255,107,107,0.1)',
                  border: '1px solid rgba(255,107,107,0.3)',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  color: '#ff6b6b',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px', // 🎯 TAMAÑO: Mantener tamaño consistente
                  height: '40px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,107,107,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,107,107,0.1)';
                }}
                title="Toggle Chat List"
              >
                <MessageSquare size={18} />
              </button>

              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message here..."
                style={{
                  flexGrow: '1',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '12px 16px',
                  borderRadius: '24px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(5px)'
                }}
                onFocus={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.08)';
                  e.target.style.borderColor = 'rgba(255,107,107,0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.05)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                }}
              />
              <button
                type="submit"
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '24px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  boxShadow: '0 4px 12px rgba(255,107,107,0.3)',
                  transition: 'all 0.3s ease',
                  marginLeft: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(255,107,107,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,107,107,0.3)';
                }}
              >
                &#10148;
                <span style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: '0' }}>Send</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 💬 MODAL DE GESTIÓN DE CHATS - DISEÑO MODERNO */}
      {showChatsModal && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.95) 100%)',
          backdropFilter: 'blur(12px)',
          zIndex: '50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'modalFadeIn 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
          onClick={() => setShowChatsModal(false)}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(30,30,46,0.98) 0%, rgba(20,20,30,0.95) 50%, rgba(15,15,25,0.98) 100%)',
            borderRadius: '20px',
            padding: '0',
            width: '85%',
            maxWidth: '600px',
            maxHeight: '75vh',
            boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08)',
            border: 'none',
            position: 'relative',
            animation: 'modalSlideUp 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            overflow: 'hidden'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✨ Header del modal compacto */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(107,191,255,0.15) 0%, rgba(64,224,208,0.1) 50%, rgba(138,43,226,0.08) 100%)',
              padding: '18px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.08)'
            }}>
              <div>
                <h3 style={{
                  margin: '0',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '600',
                  letterSpacing: '-0.3px'
                }}>
                  My Conversations
                </h3>
              </div>
              <button
                onClick={() => setShowChatsModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '12px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* 🆕 Botón para nuevo chat */}
            <div style={{
              padding: '20px 24px 12px 24px'
            }}>
              <button
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
                  color: 'white',
                  padding: '14px',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '15px',
                  fontWeight: '600',
                  boxShadow: '0 6px 20px rgba(255,107,107,0.3)',
                  transition: 'all 0.3s ease',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(255,107,107,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255,107,107,0.3)';
                }}
                onClick={() => {
                  handleNewChatClick();
                  setShowChatsModal(false);
                }}
              >
                ✨ New Chat
              </button>
            </div>



            {/* 📋 Lista de chats con propiedades */}
            <div style={{
              padding: '0 20px 20px 20px',
              maxHeight: '50vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.2) transparent'
            }}>
              {getFilteredChats().length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '14px'
                }}>
                  No chats available for this character
                </div>
              ) : (
                getFilteredChats().map((chat) => {
                  // Calcular propiedades del chat
                  // Priorizar fecha de actualización para mostrar actividad reciente
                  const displayDate = new Date(chat.updatedAt || chat.createdAt || Date.now());
                  const messageCount = chat.messages?.length || 0;
                  const isActive = activeChat === chat._id;

                  return (
                    <div
                      key={chat._id}
                      onClick={() => {
                        setActiveChat(chat._id);
                        fetchMessages(chat._id);
                        if (chat.characterId) {
                          const character = characters.find(c => c._id === chat.characterId._id);
                          if (character) {
                            setSelectedCharacter(character);
                          }
                        }
                        setShowChatsModal(false);
                      }}
                      style={{
                        position: 'relative',
                        cursor: 'pointer',
                        background: isActive
                          ? 'linear-gradient(135deg, rgba(255,107,107,0.2) 0%, rgba(255,71,87,0.15) 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                        border: isActive
                          ? '2px solid rgba(255,107,107,0.6)'
                          : '2px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        padding: '16px',
                        marginBottom: '12px',
                        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        backdropFilter: 'blur(20px)',
                        boxShadow: isActive
                          ? '0 8px 32px rgba(255,107,107,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                          : '0 4px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)';
                          e.currentTarget.style.border = '2px solid rgba(255,255,255,0.15)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)';
                          e.currentTarget.style.border = '2px solid rgba(255,255,255,0.08)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.05)';
                        }
                      }}
                    >
                      {/* Header del chat con avatar y título */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        {chat.characterId ? (
                          <img
                            src={chat.characterId.avatar}
                            alt={chat.characterId.name}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '2px solid rgba(255,255,255,0.2)'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #6c7ce7, #a55eea)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px'
                          }}>
                            💬
                          </div>
                        )}

                        <div style={{
                          flex: 1,
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: '600',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {chat.characterId ? chat.characterId.name : (chat.title || 'Chat without character')}
                          </div>
                        </div>

                        {/* Botón de eliminar */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Are you sure you want to delete this chat?')) {
                              deleteChat(chat._id);
                            }
                          }}
                          style={{
                            background: 'rgba(255,99,99,0.1)',
                            border: '1px solid rgba(255,99,99,0.3)',
                            borderRadius: '8px',
                            padding: '6px',
                            cursor: 'pointer',
                            color: '#ff6363',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255,99,99,0.2)';
                            e.currentTarget.style.color = '#ff4757';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255,99,99,0.1)';
                            e.currentTarget.style.color = '#ff6363';
                          }}
                          title="Delete chat"
                        >
                          🗑️
                        </button>
                      </div>

                      {/* Propiedades del chat */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '16px',
                          flex: 1
                        }}>
                          {/* Fecha de creación */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{
                              fontSize: '12px',
                              color: 'rgba(255,255,255,0.4)'
                            }}>📅</span>
                            <span style={{
                              fontSize: '12px',
                              color: 'rgba(255,255,255,0.6)',
                              fontWeight: '500'
                            }}>
                              {displayDate.toLocaleDateString('en-US', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit'
                              })}
                            </span>
                          </div>

                          {/* Cantidad de mensajes */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{
                              fontSize: '12px',
                              color: 'rgba(255,255,255,0.4)'
                            }}>💬</span>
                            <span style={{
                              fontSize: '12px',
                              color: 'rgba(255,255,255,0.6)',
                              fontWeight: '500'
                            }}>
                              {messageCount} msg{messageCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        {/* Indicador de chat activo */}
                        {isActive && (
                          <div
                            style={{
                              background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              color: 'white',
                              fontWeight: '600',
                              boxShadow: '0 3px 10px rgba(255,107,107,0.6), inset 0 1px 0 rgba(255,255,255,0.2)',
                              animation: 'pulse 2s infinite'
                            }}
                          >
                            ✓
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🎭 MODAL DE SELECCIÓN DE PERSONAJES - DISEÑO MODERNO */}
      {showCharacterCarousel && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.95) 100%)',
          backdropFilter: 'blur(12px)',
          zIndex: '50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'modalFadeIn 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
          onClick={() => setShowCharacterCarousel(false)}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(30,30,46,0.98) 0%, rgba(20,20,30,0.95) 50%, rgba(15,15,25,0.98) 100%)',
            borderRadius: '20px',
            padding: '0',
            width: '85%',
            maxWidth: '650px',
            maxHeight: '70vh',
            boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08)',
            border: 'none',
            position: 'relative',
            animation: 'modalSlideUp 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            overflow: 'hidden'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✨ Header del modal compacto */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255,107,107,0.15) 0%, rgba(255,71,87,0.1) 50%, rgba(138,43,226,0.08) 100%)',
              padding: '18px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.08)'
            }}>
              <div>
                <h3 style={{
                  margin: '0',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '600',
                  letterSpacing: '-0.3px'
                }}>
                  Select Character
                </h3>
              </div>
              <button
                onClick={() => setShowCharacterCarousel(false)}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '12px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* 🎨 Grid de personajes compacto */}
            <div style={{
              padding: '20px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '12px',
              maxHeight: '55vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.2) transparent'
            }}>
              {characters.map((character) => (
                <div
                  key={character._id}
                  onClick={() => {
                    selectCharacter(character);
                    setShowCharacterCarousel(false);
                  }}
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    background: selectedCharacter?._id === character._id
                      ? 'linear-gradient(135deg, rgba(255,107,107,0.2) 0%, rgba(255,71,87,0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                    border: selectedCharacter?._id === character._id
                      ? '2px solid rgba(255,107,107,0.6)'
                      : '2px solid rgba(255,255,255,0.08)',
                    borderRadius: '16px',
                    padding: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: selectedCharacter?._id === character._id
                      ? '0 8px 32px rgba(255,107,107,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                      : '0 4px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.05)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCharacter?._id !== character._id) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)';
                      e.currentTarget.style.border = '2px solid rgba(255,255,255,0.15)';
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCharacter?._id !== character._id) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)';
                      e.currentTarget.style.border = '2px solid rgba(255,255,255,0.08)';
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.05)';
                    }
                  }}
                  title={character.description}
                >
                  {/* Avatar con efecto glassmorphism */}
                  <div style={{
                    position: 'relative',
                    width: '70px',
                    height: '70px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    boxShadow: selectedCharacter?._id === character._id
                      ? '0 0 20px rgba(255,107,107,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                      : '0 6px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)'
                  }}>
                    <img
                      src={character.avatar}
                      alt={character.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'all 0.4s ease'
                      }}
                    />
                  </div>

                  {/* Nombre del personaje */}
                  <div style={{
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    letterSpacing: '-0.2px',
                    lineHeight: '1.2'
                  }}>
                    {character.name}
                  </div>

                  {/* Descripción corta */}
                  <div style={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '11px',
                    fontWeight: '400',
                    textAlign: 'center',
                    lineHeight: '1.3',
                    maxWidth: '110px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {character.description}
                  </div>

                  {/* Indicador de selección */}
                  {selectedCharacter?._id === character._id && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: '600',
                        boxShadow: '0 4px 12px rgba(255,107,107,0.6), inset 0 1px 0 rgba(255,255,255,0.2)',
                        animation: 'pulse 2s infinite'
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de información del personaje */}
      {showCharacterInfoModal && selectedCharacter && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000',
          backdropFilter: 'blur(8px)'
        }}
          onClick={() => setShowCharacterInfoModal(false)} // Cerrar al hacer click fuera
        >
          <div style={{
            background: 'linear-gradient(135deg, #2a2a3e 0%, #1f1f2e 100%)',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            position: 'relative',
            color: 'white'
          }}
            onClick={(e) => e.stopPropagation()} // Evitar que se cierre al hacer click dentro
          >
            {/* Botón de cerrar */}
            <button
              onClick={() => setShowCharacterInfoModal(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(255,107,107,0.1)',
                border: '1px solid rgba(255,107,107,0.3)',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                cursor: 'pointer',
                color: '#ff6b6b',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,107,107,0.2)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,107,107,0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>

            {/* Avatar grande */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid rgba(255,107,107,0.3)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
              }}>
                <img
                  src={selectedCharacter.avatar}
                  alt={selectedCharacter.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </div>

            {/* Nombre del personaje */}
            <h2 style={{
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '15px',
              background: 'linear-gradient(135deg, #ff6b6b, #6bbfff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {selectedCharacter.name}
            </h2>

            {/* Created By Subtitle */}
            <p style={{
              textAlign: 'center',
              fontSize: '12px',
              color: 'rgba(255,255,255,0.6)',
              marginTop: '-10px',
              marginBottom: '20px',
              fontWeight: '500'
            }}>
              Created by: <span style={{ color: '#6bbfff' }}>
                {selectedCharacter.hideCreator
                  ? 'Anonymous'
                  : (selectedCharacter.userId && typeof selectedCharacter.userId === 'object' && selectedCharacter.userId.username
                    ? selectedCharacter.userId.username
                    : 'System')}
              </span>
            </p>

            {/* Descripción */}
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#6bbfff'
              }}>
                📜 Descripción
              </h3>
              <p style={{
                fontSize: '14px',
                lineHeight: '1.5',
                color: 'rgba(255,255,255,0.8)',
                margin: '0'
              }}>
                {selectedCharacter.description}
              </p>
            </div>

            {/* Personalidad */}
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#ff6b6b'
              }}>
                🎆 Personalidad
              </h3>
              <p style={{
                fontSize: '14px',
                lineHeight: '1.5',
                color: 'rgba(255,255,255,0.8)',
                margin: '0'
              }}>
                {selectedCharacter.personality}
              </p>
            </div>

            {/* Tags */}
            {selectedCharacter.tags && selectedCharacter.tags.length > 0 && (
              <div style={{
                marginBottom: '20px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '10px',
                  color: '#6bbfff'
                }}>
                  🏷️ Características
                </h3>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  {selectedCharacter.tags.map((tag, index) => (
                    <span key={index} style={{
                      background: 'linear-gradient(135deg, rgba(255,107,107,0.2), rgba(107,191,255,0.2))',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Información adicional */}
            <div style={{
              padding: '15px',
              background: 'rgba(107,191,255,0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(107,191,255,0.2)'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '10px',
                color: '#6bbfff'
              }}>
                ✨ Interaction Tips
              </h3>
              <ul style={{
                fontSize: '13px',
                lineHeight: '1.4',
                color: 'rgba(255,255,255,0.8)',
                paddingLeft: '20px',
                margin: '0'
              }}>
                <li>Talk with {selectedCharacter.name} according to their personality</li>
                <li>Use the regenerate button to get different responses</li>
                <li>Navigate between versions using the message controls</li>
                <li>Each character has their own unique conversation style</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* 🛠️ Dynamic Sidebar Integration */}
      <Sidebar
        isOpen={showNavigationSidebar}
        onClose={() => setShowNavigationSidebar(false)}
        mode="chat"
        onHome={() => navigate('/')}
        onProfileUpdate={fetchUserProfile}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        onLogin={() => navigate('/')}
        onLogout={async () => {
          try {
            await fetch(`${API_URL}/logout`, { method: 'POST', credentials: 'include' });
            navigate('/');
            window.location.reload();
          } catch (error) {
            console.error('Logout error:', error);
          }
        }}
        userData={{
          username: userStats?.username || loggedUser || 'Guest',
          avatar: userStats?.avatar || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/erza-GgLDNYG7BpWQjSiCdY1Yya2Lrhgjxv.png',
          isPro: userStats?.subscription?.tier && userStats.subscription.tier !== 'Free',
          usage: userStats?.usage,
          gender: userStats?.gender,
          birthday: userStats?.birthday,
          bio: userStats?.bio,
          email: userStats?.email,
          isOAuthUser: userStats?.isOAuthUser,
          hasPassword: userStats?.hasPassword
        }}
        onOpenPricing={() => setShowPricingModal(true)}
      />

      {/* 🤖 LLM Selection Modal */}
      <LLMSelectionModal
        isOpen={showLLMModal}
        onClose={() => setShowLLMModal(false)}
        models={LLM_MODELS}
        selectedModelId={selectedModel}
        onSelectModel={setSelectedModel}
        backgroundBlurEnabled={backgroundBlurEnabled}
        onToggleBackgroundBlur={setBackgroundBlurEnabled}
        fullScreenChatEnabled={fullScreenChatEnabled}
        onToggleFullScreenChat={setFullScreenChatEnabled}
        disableTypingEffect={disableTypingEffect}
        onToggleDisableTypingEffect={setDisableTypingEffect}
        onOpenMemory={() => setShowMemoryHubModal(true)}
      />

      {/* 🧠 Memory Hub Modal */}
      <MemoryHubModal
        isOpen={showMemoryHubModal}
        onClose={() => setShowMemoryHubModal(false)}
        onBack={() => {
          setShowMemoryHubModal(false);
          setShowLLMModal(true);
        }}
        onOpenAutoSummary={() => {
          setShowMemoryHubModal(false);
          setShowMemoryModal(true);
        }}
        onOpenUserPersona={() => {
          setShowMemoryHubModal(false);
          setShowUserPersonaModal(true);
        }}
      />

      <UserPersonaModal
        isOpen={showUserPersonaModal}
        onClose={() => setShowUserPersonaModal(false)}
        onBack={() => {
          setShowUserPersonaModal(false);
          setShowMemoryHubModal(true);
        }}
      />

      {/* 🧠 Memory Modal (Auto-Summary) */}
      <MemoryModal
        isOpen={showMemoryModal}
        onClose={() => setShowMemoryModal(false)}
        onBack={() => {
          setShowMemoryModal(false);
          setShowMemoryHubModal(true);
        }}
        chatId={activeChat || ''}
        messages={messages}
      />

      {/* 💎 Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        currentTier={userStats?.subscription?.tier}
      />

      {/* ⚠️ Limit Reached Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        onOpenPricing={() => { setShowLimitModal(false); setShowPricingModal(true); }}
        dailyRemaining={userStats?.usage?.messagesAvailable}
        boosters={userStats?.usage?.boosterMessages}
        nextResetAt={userStats?.usage?.lastDailyReset ? new Date(new Date(userStats.usage.lastDailyReset).getTime() + 24 * 60 * 60 * 1000) : undefined}
        hasMembership={userStats?.subscription?.tier && userStats.subscription.tier !== 'free'}
      />

    </div>
  )
}
